import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Popover,
  Paper,
  Tooltip,
} from "@mui/material";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import { useTranslation } from "react-i18next";
import { colorPalette } from "../utils";

// Predefined color palette
// const colorPalette = [
//   "#FF6B6B",
//   "#4DABF7",
//   "#51CF66",
//   "#FFD43B",
//   "#FF8CC8",
//   "#8CE99A",
//   "#74C0FC",
//   "#FFB366",
//   "#DA77F2",
//   "#69DB7C",
//   "#339AF0",
//   "#FFE066",
//   "#F783AC",
//   "#96F2D7",
//   "#A78BFA",
//   "#FFA8A8",
//   "#63E6BE",
//   "#74C0FC",
//   "#FFEC99",
//   "#D0BFFF",
//   "#FF9F43",
//   "#00D4AA",
//   "#5F27CD",
//   "#FF3838",
//   "#2E86AB",
//   "#A23B72",
//   "#F18F01",
//   "#C73E1D",
//   "#6A994E",
//   "#277DA1",
//   "#F8961E",
//   "#90323D",
//   "#4D5DB5",
//   "#06FFA5",
//   "#F72585",
// ];

interface ColorPickerProps {
  value: string;
  onChange?: (color: string) => void;
  label?: string;
  disabled?: boolean;
  showHexValue?: boolean;
  customColors?: string[];
  size?: "small" | "medium" | "large";
  variant?: "standard" | "outlined";
  showColorSuggestions?: boolean;
}

const CustomColorPicker: React.FC<ColorPickerProps> = ({
  value,
  onChange,
  label = "",
  disabled = false,
  showHexValue = true,
  customColors,
  size = "medium",
  variant = "standard",
  showColorSuggestions = true,
}) => {
  const { t } = useTranslation();
  const [colorPickerAnchor, setColorPickerAnchor] =
    useState<HTMLElement | null>(null);

  const colors = customColors || colorPalette;

  const sizeConfig = {
    small: { box: 32, icon: 16, columns: 6, maxWidth: 240 },
    medium: { box: 40, icon: 20, columns: 7, maxWidth: 280 },
    large: { box: 48, icon: 24, columns: 8, maxWidth: 320 },
  };

  const config = sizeConfig[size];

  const handleColorPickerOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setColorPickerAnchor(event.currentTarget);
    }
  };

  const handleColorPickerClose = () => {
    setColorPickerAnchor(null);
  };

  const handleColorSelect = (color: string) => {
    onChange(color);
    handleColorPickerClose();
  };

  const colorBoxJsx = (
    <Box
      sx={{
        width: config.box,
        height: config.box,
        backgroundColor: value,
        borderRadius: 1,
        border:
          variant === "outlined" ? "2px solid #e0e0e0" : "1px solid #e0e0e0",
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        opacity: disabled ? 0.6 : 1,
        "&:hover": disabled
          ? {}
          : {
              border:
                variant === "outlined"
                  ? "2px solid #1976d2"
                  : "1px solid #1976d2",
            },
      }}
      onClick={handleColorPickerOpen}
    >
      <ColorLensIcon
        sx={{
          color: "white",
          fontSize: config.icon,
          filter: "drop-shadow(0px 1px 1px rgba(0,0,0,0.5))",
        }}
      />
    </Box>
  );

  return (
    <>
      <Box display="flex" gap={2} alignItems="center">
        {label && (
          <Typography variant="body1" sx={{ minWidth: "120px" }}>
            {label}:
          </Typography>
        )}
        <Box display="flex" alignItems="center" gap={1}>
          {disabled ? (
            <Tooltip title={t("Color selection disabled")} arrow>
              <div>
                {colorBoxJsx}
              </div>
            </Tooltip>
          ) : (
            colorBoxJsx
          )}
          {showHexValue && (
            <Typography variant="body2" color="text.secondary">
              {value}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Color Picker Popover */}
      <Popover
        open={Boolean(colorPickerAnchor)}
        anchorEl={colorPickerAnchor}
        onClose={handleColorPickerClose}
        disablePortal
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Paper sx={{ p: 2, maxWidth: config.maxWidth }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t("Choose Color")}
          </Typography>

          {/* Color Suggestions Grid - only show if showColorSuggestions is true */}
          {showColorSuggestions && (
            <Box
              display="grid"
              gridTemplateColumns={`repeat(${config.columns}, 1fr)`}
              gap={1}
              mb={2}
            >
              {colors.map((color) => (
                <Box
                  key={color}
                  sx={{
                    width: 30,
                    height: 30,
                    backgroundColor: color,
                    borderRadius: 1,
                    cursor: "pointer",
                    border:
                      value === color
                        ? "3px solid #1976d2"
                        : "1px solid #e0e0e0",
                    "&:hover": {
                      transform: "scale(1.1)",
                      transition: "transform 0.2s",
                    },
                  }}
                  onClick={() => handleColorSelect(color)}
                />
              ))}
            </Box>
          )}

          {/* Custom color input */}
          <Box mt={showColorSuggestions ? 0 : 2}>
            <TextField
              label={t("Custom Color")}
              type="color"
              value={value}
              onChange={(e) => handleColorSelect(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                sx: { height: 40 },
              }}
            />
          </Box>
        </Paper>
      </Popover>
    </>
  );
};

export default CustomColorPicker;
